import { useState, useEffect } from 'react';
import { api } from '../api';

/**
 * Lawyer-first scheme picker — 4 cards backed by
 * /documents/case/{caseId}/reorganize/schemes. The endpoint returns per-case
 * readiness + a Recommended chip on whichever scheme has the cleanest
 * fit. Selecting a card calls `onPick(scheme.key)` so the drawer sets
 * its `strategy` state — the wire enum to the reorganize graph.
 */
export default function SchemePicker({ caseId, scope, sourceFolder, excludeFolders, selected, onPick }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Serialize so the effect re-runs when the chosen folder(s) change.
  const excludeKey = (excludeFolders || []).join('|');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Translate the UI scope to the wire enum and pass the folder scoping
    // params so readiness reflects the docs that will actually be touched
    // (split → only the chosen folder), not the whole case.
    const wireScope = scope === 'split' ? 'redistribute' : scope;
    const qs = new URLSearchParams();
    qs.set('scope', wireScope);
    if (scope === 'split' && sourceFolder) qs.set('source_folder', sourceFolder);
    if (scope === 'exclude') {
      (excludeFolders || []).forEach(f => qs.append('exclude_folders', f));
    }
    api.get(`/documents/case/${caseId}/reorganize/schemes?${qs.toString()}`)
      .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [caseId, scope, sourceFolder, excludeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select the recommended card on first load, but don't override if
  // the user has already picked one. Lets the user see the chip + accept
  // it implicitly with one click on Propose, or change it deliberately.
  useEffect(() => {
    if (data?.recommended && !selected) {
      onPick(data.recommended);
    }
  }, [data?.recommended]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <div className="reorg-label">Loading scheme options…</div>;
  }
  if (error) {
    return <div className="reorg-label" style={{ color: '#c33' }}>Couldn't load schemes: {error}</div>;
  }
  if (!data || !data.schemes?.length) {
    return <div className="reorg-label">No schemes available for this case.</div>;
  }

  const badgeStyles = {
    ready:        { color: '#1a7a1a', label: '✅ Ready' },
    limited:      { color: '#b97b1b', label: '⚠️ Limited' },
    insufficient: { color: '#666',    label: '⛔ Not enough signal' },
    disabled:     { color: '#888',    label: '⛔ Coming soon' },
  };

  return (
    <div>
      <div className="reorg-label">How should Arc group your documents?</div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 10,
        marginTop: 6,
      }}>
        {data.schemes.map(s => {
          const isSelected = selected === s.key;
          const isDisabled = s.disabled;
          const badge = badgeStyles[s.readiness] || badgeStyles.insufficient;
          const cardStyle = {
            position: 'relative',
            padding: 10,
            border: `1.5px solid ${isSelected ? '#3b6bd6' : '#d0d4dc'}`,
            borderRadius: 6,
            background: isDisabled ? '#f5f6f8' : (isSelected ? '#eef3fc' : '#fff'),
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            opacity: isDisabled ? 0.6 : 1,
            transition: 'border-color 0.1s, background 0.1s',
          };
          return (
            <div
              key={s.key}
              style={cardStyle}
              onClick={() => { if (!isDisabled) onPick(s.key); }}
              role="button"
              tabIndex={isDisabled ? -1 : 0}
              aria-pressed={isSelected}
              aria-disabled={isDisabled}
            >
              {s.recommended && (
                <span style={{
                  position: 'absolute', top: 6, right: 8,
                  fontSize: 10, fontWeight: 600, color: '#3b6bd6',
                  background: '#e1ebfc', padding: '1px 6px', borderRadius: 8,
                }}>
                  RECOMMENDED
                </span>
              )}
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
                {s.label}
              </div>
              <div style={{ fontSize: 11, color: '#555', marginBottom: 8, lineHeight: 1.35 }}>
                {s.description}
              </div>
              <ul style={{
                margin: '0 0 8px 0', paddingLeft: 14,
                fontSize: 11, color: '#444', lineHeight: 1.5,
              }}>
                {s.sample_folders.slice(0, 4).map(p => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
              <div style={{ fontSize: 10, color: badge.color, fontWeight: 500 }}
                   title={s.reason}>
                {badge.label}
                {s.coverage_pct > 0 && s.readiness !== 'disabled' && (
                  <span style={{ marginLeft: 6, color: '#888', fontWeight: 400 }}>
                    {Math.round(s.coverage_pct * 100)}% coverage
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {data.doc_count > 0 && (
        <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
          Readiness computed across {data.doc_count} document{data.doc_count === 1 ? '' : 's'} in scope.
        </div>
      )}
    </div>
  );
}


